import { type ReactNode, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AccessDeniedState } from '@/components/auth/access-denied-state';
import { LoadingScreen } from '@/components/loading-screen';
import { PermissionsType, USER_TYPE } from '@/lib/data';
import {
  buildWebPermissionMap,
  getResourceFromPath,
} from '@/lib/utils/get-page-permissions';
import { useAccessDeniedStore } from '@/store/access-denied';
import { useAuthStore } from '@/store/auth';

type Props = {
  required?: PermissionsType[];
  children: ReactNode;
};

export const PermissionGuard = ({
  required = [PermissionsType.read],
  children,
}: Props) => {
  // Component hooks
  const location = useLocation();

  // Component stores
  const { user, isLoading } = useAuthStore();
  const deniedPhase = useAccessDeniedStore((s) => s.phase);
  const deniedPath = useAccessDeniedStore((s) => s.path);

  // Component defaults
  const resource = getResourceFromPath(location.pathname);
  const permissionMap = useMemo(() => {
    const webPermissions = user?.permissions?.web;

    if (!webPermissions) return null;

    return buildWebPermissionMap(webPermissions);
  }, [user]);

  // A denial belongs to the page it was reported on: drop it when the user
  // moves to another path or leaves (also to a page without this guard).
  // The guard also registers its path, so a 403 on a route without one is
  // not held for a denied state that cannot render
  useEffect(() => {
    const pathname = location.pathname;
    const { path, reset, setGuardedPath } = useAccessDeniedStore.getState();

    if (path !== null && path !== pathname) reset();

    // a refusal is held only where this guard can render it
    setGuardedPath(pathname);

    return () => {
      const denied = useAccessDeniedStore.getState();

      if (denied.path === pathname) denied.reset();
      denied.releaseGuardedPath(pathname);
    };
  }, [location.pathname]);

  if (isLoading || !permissionMap) {
    return <LoadingScreen />;
  }

  // Redirect glazier users from /claims to /jobs
  if (
    user?.userType === USER_TYPE.GLAZIER &&
    location.pathname.startsWith('/claims')
  ) {
    return (
      <Navigate to={location.pathname.replace(/^\/claims/, '/jobs')} replace />
    );
  }

  // Redirect non-glazier users from /jobs to /claims
  if (
    user?.userType !== USER_TYPE.GLAZIER &&
    location.pathname.startsWith('/jobs')
  ) {
    return (
      <Navigate to={location.pathname.replace(/^\/jobs/, '/claims')} replace />
    );
  }

  const permission = permissionMap[resource];
  const hasAccess = !!permission && required.every((key) => permission[key]);

  if (!hasAccess) {
    return <Navigate to='/403' replace state={{ from: location }} />;
  }

  // The page's own request was refused: wait for the token, don't render the page
  if (deniedPhase !== 'idle' && deniedPath === location.pathname) {
    return <AccessDeniedState />;
  }

  return <>{children}</>;
};
